import { ownCopy } from '../story/chapters';

/**
 * Storefront v6 — the adaptive section plan.
 *
 * The page infers the tenant's business model from its configuration and
 * catalogue (the tenant's `business_types` are not on any public endpoint
 * today — a follow-up), orders the homepage for that model, and drops every
 * section that has nothing to show. A merchant override (`storefront_sections`
 * on the config, when Design Studio grows it) is applied last.
 */

export const MODELS = ['restaurant', 'meal_prep', 'catering', 'bakery'];

export const inferBusinessModel = ({ brand = {}, plans = [], items = [], cateringPackages = [] } = {}) => {
  if (MODELS.includes(brand.business_model)) return brand.business_model;
  const hasPlans = brand.mealplans_enabled !== false && plans.length > 0;
  const hasCatering = brand.catering_enabled === true && cateringPackages.length > 0;
  const hasMenu = brand.menu_enabled !== false && items.length > 0;
  if (hasPlans) return 'meal_prep';
  if (hasCatering && !hasMenu) return 'catering';
  if (hasMenu && plans.every((p) => p.pickup_only) && plans.length > 0) return 'bakery';
  return 'restaurant';
};

/** The section order per model. Every id maps to a section component. */
const ORDER = {
  restaurant: ['announce', 'header', 'hero', 'fulfilment', 'facts', 'categories', 'popular', 'menu', 'passes', 'plans', 'catering', 'promo', 'reviews', 'about', 'info', 'gallery', 'faq', 'closing', 'footer'],
  meal_prep: ['announce', 'header', 'hero', 'fulfilment', 'facts', 'plans', 'how', 'popular', 'promo', 'menu', 'passes', 'catering', 'reviews', 'about', 'info', 'gallery', 'faq', 'closing', 'footer'],
  catering: ['announce', 'header', 'hero', 'facts', 'catering', 'how', 'popular', 'reviews', 'about', 'info', 'gallery', 'faq', 'closing', 'footer'],
  bakery: ['announce', 'header', 'hero', 'fulfilment', 'facts', 'categories', 'popular', 'menu', 'plans', 'promo', 'reviews', 'about', 'info', 'gallery', 'faq', 'closing', 'footer'],
};

/**
 * buildSectionPlan(model, facts) → ordered ids that have something to show.
 * `facts` carries booleans computed by the page from real data.
 */
export const buildSectionPlan = (model, facts = {}, override = null) => {
  const base = ORDER[model] || ORDER.restaurant;
  const order = applyOverride(base, override);
  const show = {
    announce: !!facts.announce,
    header: true,
    hero: facts.hero !== false,
    fulfilment: !!facts.fulfilment,
    facts: !!facts.facts,
    closing: !!facts.closing,
    categories: !!facts.categories,
    popular: !!facts.popular,
    menu: !!facts.menu,
    plans: !!facts.plans,
    how: !!facts.how,
    passes: !!facts.passes,
    catering: !!facts.catering,
    promo: !!facts.promo,
    reviews: !!facts.reviews,
    about: !!facts.about,
    info: !!facts.info,
    gallery: !!facts.gallery,
    faq: !!facts.faq,
    footer: true,
  };
  return order.filter((id) => show[id]);
};

/** The hero's primary + secondary call to action per model (merchant copy wins). */
export const heroCtasFor = (model, brand = {}, facts = {}) => {
  // the platform's pass copy ("Browse passes") is not the merchant's: a default that
  // names passes falls through to the model's neutral verb unless the store sells them
  const primary = ownCopy(brand, 'hero_cta_primary', { passes: !!facts.passes }) || '';
  const secondary = ownCopy(brand, 'hero_cta_secondary', { passes: !!facts.passes }) || '';
  const defaults = {
    restaurant: ['Order now', 'View menu'],
    meal_prep: ['Choose a plan', 'How it works'],
    catering: ['Order catering', 'See packages'],
    bakery: ['Order for pickup', 'View menu'],
  }[model] || ['Order now', 'View menu'];
  return {
    primary: primary || defaults[0],
    secondary: secondary || defaults[1],
    primaryTarget: model === 'meal_prep' ? 'plans' : model === 'catering' ? 'catering' : (facts.orderable ? 'order' : facts.plans ? 'plans' : 'menu'),
    secondaryTarget: model === 'meal_prep' ? 'how' : model === 'catering' ? 'catering' : 'menu',
  };
};

export default buildSectionPlan;

/** The chrome nobody reorders: the hero sits under the floating header (a negative
 *  top margin), so it can only ever be first. */
const FIXED_TOP = ['announce', 'header', 'hero'];
const FIXED_BOTTOM = ['footer'];
const movable = (ids) => ids.filter((id) => !FIXED_TOP.includes(id) && !FIXED_BOTTOM.includes(id));

/**
 * A merchant's saved order governs the MIDDLE of the page: unknown ids are
 * dropped, the chrome keeps its place, and a section the saved list never
 * knew (enabled after the order was saved) still renders, after the ordered
 * ones, instead of vanishing.
 */
export const applyOverride = (base, override) => {
  if (!Array.isArray(override) || !override.length) return base;
  const wanted = movable(override.filter((id) => base.includes(id)));
  const rest = movable(base).filter((id) => !wanted.includes(id));
  return [...FIXED_TOP.filter((id) => base.includes(id)), ...wanted, ...rest, ...FIXED_BOTTOM.filter((id) => base.includes(id))];
};

/** The sections a merchant can reorder in the Design Studio (labels for the editor). */
export const V6_MOVABLE_SECTIONS = [
  { id: 'fulfilment', label: 'Pickup / delivery card' },
  { id: 'facts', label: 'Facts strip' },
  { id: 'categories', label: 'Category rail' },
  { id: 'popular', label: 'Popular dishes' },
  { id: 'plans', label: 'Meal plans' },
  { id: 'how', label: 'How it works' },
  { id: 'promo', label: 'Promo band' },
  { id: 'menu', label: 'Menu' },
  { id: 'passes', label: 'Meal passes' },
  { id: 'catering', label: 'Catering' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'about', label: 'About' },
  { id: 'info', label: 'Hours, address, ordering' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'faq', label: 'FAQ' },
  { id: 'closing', label: 'Closing call to action' },
];
